import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Minus, ShoppingCart, Eye, Phone, FileText, LogIn } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { formatPrice } from '../../lib/utils';
import { toast } from 'sonner';

export const ProductCard = ({ product }) => {
    const { addItem } = useCart();
    const { isAuthenticated } = useAuth();
    const [quantity, setQuantity] = useState(product.min_order || 1);

    const isOrderable = product.ready_to_order && product.price > 0;

    const handleAddToCart = (e) => {
        e.preventDefault();
        if (!isAuthenticated) {
            toast.error('Connectez-vous pour commander');
            return;
        }
        addItem(product, quantity);
        toast.success(
            isOrderable
                ? `${product.name} ajoute a la commande`
                : `${product.name} ajoute a la demande de prix`
        );
        setQuantity(product.min_order || 1);
    };

    return (
        <Card className="group overflow-hidden border-0 bg-white/80 backdrop-blur-sm shadow-sm hover:shadow-xl transition-all duration-500 rounded-2xl" data-testid={`product-card-${product.id}`}>
            <Link to={`/produit/${product.id}`}>
                <div className="aspect-[4/3] overflow-hidden relative">
                    <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    {product.origin && (
                        <Badge className="absolute top-3 left-3 bg-jungle/90 text-sand rounded-full px-3">
                            {product.origin}
                        </Badge>
                    )}
                    {product.is_weekly_offer && (
                        <div className="absolute top-0 right-0">
                            <div className="bg-gradient-to-bl from-red-500 to-amber-500 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-bl-xl shadow-md">
                                Promo
                            </div>
                        </div>
                    )}
                    {isOrderable && !product.is_weekly_offer && (
                        <Badge className="absolute top-3 right-3 bg-emerald-600 text-white rounded-full px-3" data-testid={`badge-orderable-${product.id}`}>
                            Commande directe
                        </Badge>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-jungle/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-6">
                        <Button variant="secondary" size="sm" className="bg-white/90 text-jungle rounded-full shadow-lg">
                            <Eye className="w-4 h-4 mr-2" />
                            Voir details
                        </Button>
                    </div>
                </div>
            </Link>
            <CardContent className="p-5">
                <Link to={`/produit/${product.id}`}>
                    <h3 className="font-heading font-semibold text-jungle group-hover:text-leaf transition-colors truncate text-lg">
                        {product.name}
                    </h3>
                </Link>
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2 h-10">
                    {product.description}
                </p>
                <div className="flex items-center justify-between mt-4">
                    {isOrderable ? (
                        <div data-testid={`price-${product.id}`}>
                            <span className="text-xl font-bold text-leaf">{formatPrice(product.price)}</span>
                            <span className="text-sm text-muted-foreground ml-1">/ {product.unit}</span>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 text-amber-700" data-testid={`price-${product.id}`}>
                            <Phone className="w-4 h-4" />
                            <span className="text-sm font-medium">Prix sur demande</span>
                        </div>
                    )}
                    <Badge variant="outline" className="text-xs border-jungle/20 text-jungle/70 rounded-full">
                        Min: {product.min_order} {product.unit}
                    </Badge>
                </div>
                
                {isAuthenticated ? (
                    <div className="flex items-center gap-2 mt-5">
                        <div className="flex items-center border border-jungle/20 rounded-xl bg-white">
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-9 w-9 hover:bg-jungle/10 rounded-l-xl"
                                onClick={() => setQuantity(Math.max(product.min_order, quantity - 1))}
                                data-testid={`decrease-qty-${product.id}`}
                            >
                                <Minus className="h-3 w-3" />
                            </Button>
                            <span className="w-10 text-center text-sm font-medium">{quantity}</span>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-9 w-9 hover:bg-jungle/10 rounded-r-xl"
                                onClick={() => setQuantity(quantity + 1)}
                                data-testid={`increase-qty-${product.id}`}
                            >
                                <Plus className="h-3 w-3" />
                            </Button>
                        </div>
                        <Button 
                            onClick={handleAddToCart} 
                            className={`flex-1 rounded-xl h-9 text-xs ${
                                isOrderable
                                    ? 'bg-jungle hover:bg-jungle/90 text-sand'
                                    : 'bg-amber-600 hover:bg-amber-700 text-white'
                            }`}
                            data-testid={`add-to-cart-${product.id}`}
                        >
                            {isOrderable ? (
                                <>
                                    <ShoppingCart className="h-3.5 w-3.5 mr-1 flex-shrink-0" />
                                    <span className="truncate">Ajouter</span>
                                </>
                            ) : (
                                <>
                                    <FileText className="h-3.5 w-3.5 mr-1 flex-shrink-0" />
                                    <span className="truncate">Devis</span>
                                </>
                            )}
                        </Button>
                    </div>
                ) : (
                    <div className="mt-5">
                        <Link to="/connexion" className="flex items-center justify-center gap-2 w-full rounded-xl h-9 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-700 text-sm font-medium transition-colors" data-testid={`login-to-order-${product.id}`}>
                            <LogIn className="h-4 w-4" />
                            Connectez-vous pour commander
                        </Link>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
